import React, { useCallback, useState } from 'react'

import { css } from 'design'
import { withTooltip } from 'primitives/tooltip'
import { cn } from 'util/cn'

import ErrorBoundary from 'containers/error_boundary'

export type ButtonVariantType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'success'
  | 'info'
  | 'warning'
  | 'question'
  | 'clean'
  | 'blank'
  | 'link'
export type ButtonProps = {
  variant?: ButtonVariantType
  shape?: 'default' | 'square' | 'circle' | 'pill'
  size?: 'small' | 'medium' | 'large' | 'fluid'
  saturated?: boolean
  toggled?: boolean
  stopPropagation?: boolean
} & React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>

const guidance = [
  {
    key: 'danger',
    color: 'red',
  },
  {
    key: 'warning',
    color: 'orange',
  },
  {
    key: 'success',
    color: 'green',
  },
  {
    key: 'info',
    color: 'blue',
  },
  {
    key: 'question',
    color: 'purple',
  },
]

const foregroundMap = (
  colour: string,
): { color: string; fill: string; stroke: string } => ({
  color: colour,
  fill: colour,
  stroke: colour,
})

const guidanceVariants = () => {
  const v: any = {}
  guidance.forEach(({ key, color }) => {
    v[key] = {
      background: `$${color}100`,
      ...foregroundMap(`$${color}900`),
      borderColor: `$${color}100`,
      '.topic__theme--dark &': {
        background: `$${color}100`,
        ...foregroundMap(`$${color}900`),
        borderColor: `$${color}100`,
      },
      '&[aria-pressed="true"], .topic__theme--dark &[aria-pressed="true"]': {
        background: `$${color}300`,
        ...foregroundMap(`$${color}900`),
        borderColor: `$${color}300`,
      },

      '@hover': {
        '&:hover, .topic__theme--dark &:hover': {
          background: `$${color}200`,
          borderColor: `$${color}200`,
        },
        '&[aria-pressed="true"]:hover, .topic__theme--dark &[aria-pressed="true"]:hover':
          {
            background: `$${color}200`,
            borderColor: `$${color}200`,
          },
      },
    }
  })
  return v
}

const toggledGuidanceVariants = () => {
  const v: any[] = []
  guidance.forEach(({ key, color }) => {
    v.push({
      variant: key,
      toggled: 'true',
      css: {
        background: `$${color}300`,
        ...foregroundMap(`$${color}900`),

        '@hover': {
          '&:hover': {
            background: `$${color}200`,
            borderColor: `$${color}200`,
          },
        },
      },
    })
  })
  return v
}

const saturatedGuidanceVariants = () => {
  const v: any[] = []
  guidance.forEach(({ key, color }) => {
    v.push({
      variant: key,
      saturated: 'true',
      css: {
        background: `$${color}500`,
        ...foregroundMap(`$${color}50`),
        borderColor: `$${color}500`,
        '.topic__theme--dark &': {
          background: `$${color}500`,
          ...foregroundMap(`$${color}50`),
          borderColor: `$${color}500`,
        },
        '&[aria-pressed="true"], .topic__theme--dark &[aria-pressed="true"]': {
          background: `$${color}600`,
          ...foregroundMap(`$${color}50`),
          borderColor: `$${color}600`,
        },

        '@hover': {
          '&:hover, .topic__theme--dark &:hover': {
            background: `$${color}400`,
            borderColor: `$${color}400`,
          },
          '&[aria-pressed="true"]:hover, .topic__theme--dark &[aria-pressed="true"]:hover':
            {
              background: `$${color}400`,
              borderColor: `$${color}400`,
            },
        },
      },
    })
  })
  return v
}

export const buttonStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  position: 'relative',
  cursor: 'pointer',
  userSelect: 'none',
  '-webkit-appearance': 'none',

  $$buttonSize: '$space$6',

  minHeight: '$$buttonSize',

  border: '1px solid',
  borderColor: '$grey100',
  borderRadius: '6px',

  background: 'transparent',
  ...foregroundMap('$grey950'),
  strokeWidth: 0,

  '&:focus': {
    borderRadius: '6px',
  },

  '&[aria-pressed="true"]': {
    background: '$grey800',
    ...foregroundMap('$grey50'),
  },

  '&:disabled': {
    opacity: 0.5,
    pointerEvents: 'none',
    borderColor: 'transparent',
  },

  '.topic__theme--dark &': {
    background: '$grey100',
    borderColor: 'transparent',
  },

  '.topic__theme--dark &:focus': {
    background: '$grey200',
    borderColor: '$grey400',
  },
  '.topic__theme--dark &[aria-pressed="true"]': {
    background: '$grey800',
    borderColor: '$grey50',
  },

  '@hover': {
    '&:hover': {
      borderColor: '$grey400',
    },
    '&[aria-pressed="true"]:hover': {
      background: '$grey700',
    },
    '.topic__theme--dark &:hover': {
      background: '$grey200',
      borderColor: '$grey400',
    },
    '.topic__theme--dark &[aria-pressed="true"]:hover': {
      background: '$grey700',
    },
  },

  variants: {
    variant: {
      primary: {
        background: '$accent600',
        ...foregroundMap('$accent50'),
        borderColor: '$accent600',
        '.topic__theme--dark &': {
          background: '$accent600',
          ...foregroundMap('$accent50'),
          borderColor: '$accent600',
        },
        '.topic__theme--dark &:focus': {
          $$buttonBg: '$colors$accent700',
          $$buttonBorder: '$colors$accent700',
        },
        '&[aria-pressed="true"], .topic__theme--dark &[aria-pressed="true"]': {
          background: '$accent800',
          ...foregroundMap('$accent50'),
          borderColor: '$accent800',
        },

        '@hover': {
          '&:hover, .topic__theme--dark &:hover': {
            background: '$accent700',
            borderColor: '$accent700',
          },
          '&[aria-pressed="true"]:hover, .topic__theme--dark &[aria-pressed="true"]:hover':
            {
              background: '$accent700',
              borderColor: '$accent700',
            },
        },
      },
      secondary: {
        background: '$backgroundSecondary',
      },
      tertiary: {
        background: 'transparent',
        color: '$grey900',
        fill: '$grey900',
        stroke: '$grey900',
        borderColor: 'transparent',
        '&[aria-pressed="true"]': {
          background: '$grey800',
          ...foregroundMap('$accent50'),
          borderColor: 'inherit',
        },
        '.topic__theme--dark &': {
          background: 'transparent',
          ...foregroundMap('$grey800'),
          borderColor: 'transparent',
        },

        '@hover': {
          '&:hover': {
            background: 'inherit',
            borderColor: '$grey400',
          },
          '&[aria-pressed="true"]:hover': {
            background: '$grey700',
            borderColor: '$grey700',
          },
          '.topic__theme--dark &:hover': {
            background: '$grey200',
            ...foregroundMap('$grey950'),
            borderColor: 'transparent',
          },
        },
      },
      ...guidanceVariants(),
      clean: {
        borderColor: 'transparent',
        $$buttonSize: 'unset',
        borderRadius: '2px',

        '&:focus': {
          borderRadius: '2px',
        },

        '&[aria-pressed="true"]': {
          background: '$grey800',
          ...foregroundMap('$grey50'),
        },

        '@hover': {
          '&:hover': {
            borderColor: '$grey400',
          },
          '&[aria-pressed="true"]:hover': {
            background: '$grey700',
          },
        },
      },
      blank: {
        background: 'none',
        borderColor: 'transparent',

        '@hover': {
          '&:hover': {
            borderColor: '$grey300',
          },
        },
      },
      link: {
        background: 'none !important',
        border: 'none',
        padding: 0,
        minHeight: 'unset',
        color: '$blue500',
        textDecoration: 'underline',

        '@hover': {
          '&:hover': {
            color: '$blue700',
          },
        },

        '&:visited': {
          color: '$blue500',
        },

        '.topic__theme--dark &': {
          '@hover': {
            '&:hover': {
              color: '$blue400',
            },
          },
        },
      },
    },
    toggled: {
      true: {
        background: '$grey200',
        ...foregroundMap('$grey950'),

        '@hover': {
          '&:hover': {
            background: '$grey300',
          },
        },
      },
    },
    shape: {
      default: {},
      square: {
        padding: 0,
        width: '$$buttonSize',
        height: '$$buttonSize',
      },
      circle: {
        padding: 0,
        borderRadius: '50%',
        width: '$$buttonSize',
        height: '$$buttonSize',

        '&:focus': {
          borderRadius: '50%',
        },
      },
      pill: {
        borderRadius: `calc($$buttonSize / 2)`,
        minWidth: `calc($$buttonSize * 2.5)`,

        '&:focus': {
          borderRadius: `calc($$buttonSize / 2)`,
        },
      },
    },
    size: {
      small: {
        $$buttonSize: '$space$5',
      },
      medium: {
        $$buttonSize: '$space$6',
      },
      large: {
        $$buttonSize: '$space$7',
      },
      fluid: {
        $$buttonSize: '100%',
        minWidth: '$$buttonSize',
      },
    },
    saturated: {
      true: {},
    },
  },
  compoundVariants: [
    {
      variant: 'primary',
      toggled: 'true',
      css: {
        background: '$accent700',
        ...foregroundMap('$accent50'),
        borderColor: '$accent700',

        '@hover': {
          '&:hover': {
            background: '$accent600',
            borderColor: '$accent600',
          },
        },
      },
    },
    {
      variant: 'tertiary',
      toggled: 'true',
      css: {
        background: '$grey950',
        ...foregroundMap('$grey50'),
        borderColor: 'inherit',

        '.topic__theme--dark &': {
          ...foregroundMap('$grey50'),
        },

        '@hover': {
          '&:hover': {
            background: '$grey800',
            borderColor: '$grey800',
          },

          '.topic__theme--dark &:hover': {
            ...foregroundMap('$grey50'),
          },
        },
      },
    },
    {
      variant: 'secondary',
      toggled: 'true',
      css: {
        background: '$grey950',
        ...foregroundMap('$grey50'),
        borderColor: 'inherit',

        '@hover': {
          '&:hover': {
            background: '$grey800',
            borderColor: 'inherit',
          },
        },
      },
    },
    ...toggledGuidanceVariants(),
    {
      variant: 'clean',
      toggled: 'true',
      css: {
        background: '$grey800',
        ...foregroundMap('$grey50'),

        '@hover': {
          '&:hover': {
            background: '$grey700',
          },
        },
      },
    },
    ...saturatedGuidanceVariants(),
  ],
  defaultVariants: {
    variant: 'secondary',
    shape: 'default',
    size: 'medium',
  },
})

const RawButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      disabled = false,
      variant = 'secondary',
      shape = 'default',
      size = 'medium',
      toggled = false,
      saturated = false,
      type = 'button',
      onClick,
      stopPropagation = false,
      ...props
    },
    ref,
  ) => {
    const [internalDisabled, setInternalDisabled] = useState<boolean>()

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (stopPropagation) {
          event.stopPropagation()
        }

        if (!onClick) {
          return
        }

        // If there is a long-executing onClick function, disable while executing
        const timeout = setTimeout(() => setInternalDisabled(true), 0)
        const resetInternalState = () => {
          clearTimeout(timeout)
          setInternalDisabled(false)
        }

        const handleCallback = (callback: unknown) => {
          resetInternalState()

          if (callback) {
            if (typeof callback === 'function') {
              callback()
            } else {
              ErrorBoundary.logErrorToMyService(
                new Error('callback is not a function'),
              )
            }
          }
        }

        try {
          const onResolve: any = onClick(event)

          // If the function returned is a promise, handle that
          if (onResolve?.then) {
            // Handling rejected callbacks with this Promise signature
            onResolve.then(handleCallback, handleCallback)
          } else {
            handleCallback(onResolve)
          }
        } catch (e: any) {
          ErrorBoundary.logErrorToMyService(e)
          resetInternalState()
        }
      },
      [onClick, stopPropagation],
    )

    const isDisabled = disabled || internalDisabled

    return (
      <button
        type={type}
        ref={ref}
        onClick={handleClick}
        disabled={isDisabled ? true : undefined}
        aria-pressed={toggled ? 'true' : undefined}
        className={cn(
          buttonStyles({ variant, size, shape, toggled, saturated }),
          className,
        )}
        {...props}
      >
        {children}
      </button>
    )
  },
)

RawButton.displayName = 'Button'

export const Button = withTooltip(RawButton)
